import type { Account } from "@aptos-labs/ts-sdk";
import { makeAutoObservable } from "mobx";

import type { SubAccount, Wallet } from "@src/services";
import {
  accountLoader,
  connectionFlowService,
  createAccountFromPrivateKey,
  storageService,
  subAccountService,
} from "@src/services";

import type { RootStore } from ".";

export class SubAccountsStore {
  private _allSubAccounts = new Map<string, SubAccount[]>();
  subAccountsLoaded = false;
  isLoadingSubAccount = false;
  activeTradingAccountAddress: string | null = null;
  subAccount: Account | null = null;

  private tempFundingSubAccount: SubAccount | null = null;
  private tempTradingSubAccount: SubAccount | null = null;

  constructor(private readonly rootStore: RootStore) {
    makeAutoObservable(this);
  }

  async initialize() {
    await this.loadFromSession();
  }

  // ==================== COMPUTED ====================
  get allSubAccounts() {
    return this._allSubAccounts;
  }

  get subAccountAddress() {
    return this.subAccount?.accountAddress.toString() || null;
  }

  getFundingSubAccount = (walletAddress: string | undefined): SubAccount | null => {
    if (!walletAddress) return null;
    return this._allSubAccounts.get(walletAddress)?.find((acc) => acc.type === "funding") || null;
  };

  getTradingSubAccount = (walletAddress: string | undefined): SubAccount | null => {
    if (!walletAddress) return null;
    return this._allSubAccounts.get(walletAddress)?.find((acc) => acc.type === "trading" && acc.nonce === "0") || null;
  };

  getTradingSubAccounts = (walletAddress: string | undefined): SubAccount[] => {
    if (!walletAddress) return [];
    return (this._allSubAccounts.get(walletAddress)?.filter((e) => e.type === "trading") ?? []).sort(
      (a, b) => (a.orderIndex ?? 999) - (b.orderIndex ?? 999),
    );
  };

  getNextTradingAccountNonce = (walletAddress: string | undefined): string => {
    if (!walletAddress) return "0";
    return (this._allSubAccounts.get(walletAddress)?.length ?? 0).toString();
  };

  getActiveTradingAccount = (walletAddress: string | undefined): SubAccount | null => {
    return (
      this.getTradingSubAccounts(walletAddress).find((acc) => acc.address === this.activeTradingAccountAddress) || null
    );
  };

  hasAccountsWithKeys = (walletAddress: string | undefined): boolean => {
    if (!walletAddress) return false;
    return this._allSubAccounts.get(walletAddress)?.some((acc) => acc.privateKey) ?? false;
  };

  // ==================== SETTERS ====================
  setSubAccount = (account: Account | null) => {
    this.subAccount = account;
  };

  setAllSubAccounts = (address: string, subAccounts: SubAccount[]) => {
    this._allSubAccounts.set(address, subAccounts);
  };

  setActiveTradingAccountAddress = async (address: string) => {
    this.activeTradingAccountAddress = address;
    await storageService.setActiveTradingAddress(address);
  };

  // ==================== ACCOUNT SIGNING ====================
  signAccounts = async (wallet: Wallet) => {
    if (!wallet) return;
    const result = await subAccountService.signAccountsWithMasterSignature(wallet);
    if (result) {
      this.tempFundingSubAccount = result.funding;
      this.tempTradingSubAccount = result.trading;
    }
  };

  finalizeAccountCreation = async (wallet: Wallet) => {
    if (!wallet.account || !this.tempFundingSubAccount || !this.tempTradingSubAccount) {
      throw new Error("Missing required data for account creation");
    }

    const address = wallet.account.address.toString();
    const { fundingSubAccount, tradingSubAccount } = {
      fundingSubAccount: this.tempFundingSubAccount,
      tradingSubAccount: this.tempTradingSubAccount,
    };

    const existing = this._allSubAccounts.get(address) ?? [];
    const updated = this.mergeSubAccounts(existing, fundingSubAccount, tradingSubAccount);

    this._allSubAccounts.set(address, updated);
    await storageService.setSubAccounts(this._allSubAccounts);

    this.setSubAccount(createAccountFromPrivateKey(fundingSubAccount.privateKey));
    this.activeTradingAccountAddress = tradingSubAccount.address;
    await storageService.setActiveTradingAddress(tradingSubAccount.address);

    this.tempFundingSubAccount = null;
    this.tempTradingSubAccount = null;
  };

  private mergeSubAccounts = (existing: SubAccount[], funding: SubAccount, trading: SubAccount): SubAccount[] => {
    const updated = existing.map((acc) => {
      if (acc.address.toLowerCase() === funding.address.toLowerCase()) return { ...acc, ...funding };
      if (acc.address.toLowerCase() === trading.address.toLowerCase()) return { ...acc, ...trading };
      return acc;
    });

    if (!existing.some((acc) => acc.address.toLowerCase() === funding.address.toLowerCase())) updated.push(funding);
    if (!existing.some((acc) => acc.address.toLowerCase() === trading.address.toLowerCase())) updated.push(trading);

    return updated;
  };

  // ==================== BLOCKCHAIN OPERATIONS ====================
  checkAccountsLinkedToBlockchain = async (
    walletAddress: string,
    forceRefresh = false,
    onFailure?: () => void | Promise<void>,
  ): Promise<boolean> => {
    return subAccountService.checkAccountsLinkedToBlockchain(walletAddress, forceRefresh, onFailure);
  };

  linkAccountsToBlockchain = async (wallet: Wallet) => {
    if (!wallet.account) throw new Error("No wallet connected");

    const address = wallet.account.address.toString();
    const subAccounts = this._allSubAccounts.get(address) || [];
    const funding = subAccounts.find((acc) => acc.type === "funding" && acc.privateKey);
    const trading = subAccounts.find((acc) => acc.type === "trading" && acc.nonce === "0" && acc.privateKey);

    if (!funding || !trading) throw new Error("Funding or trading account not found");

    await subAccountService.linkAccountsToBlockchain(wallet, funding, trading);
  };

  addTradingAccount = async (wallet: Wallet) => {
    if (!wallet.account) return;

    const address = wallet.account.address.toString();
    const nonce = this.getNextTradingAccountNonce(address);

    const newAccount = await subAccountService.addTradingAccount(wallet, nonce);
    const merged = [...(this._allSubAccounts.get(address) ?? []), newAccount];

    this._allSubAccounts.set(address, merged);
    await storageService.setSubAccounts(this._allSubAccounts);

    return newAccount;
  };

  fetchSubAccountsFromAptos = async (rootAddress: string): Promise<SubAccount[]> => {
    return subAccountService.fetchSubAccountsFromAptos(rootAddress);
  };

  generateTradingAccountByNonce = async (wallet: Wallet, nonce: string): Promise<SubAccount | null> => {
    return subAccountService.generateTradingAccountByNonceFromMaster(wallet, nonce);
  };

  // ==================== LOADING ====================
  loadSubAccount = async (
    address: string,
    callbacks: {
      generateRootAccountToken: () => Promise<void>;
      fetchAllPortfolios: () => Promise<void>;
    },
    forceRefresh = false,
  ) => {
    await accountLoader.loadSubAccount(
      address,
      {
        setSubAccount: this.setSubAccount,
        setAllSubAccounts: this.setAllSubAccounts,
        getAllSubAccounts: () => this._allSubAccounts,
        setSubAccountsLoaded: (v) => {
          this.subAccountsLoaded = v;
        },
        setIsLoadingSubAccount: (v) => {
          this.isLoadingSubAccount = v;
        },
        getIsLoadingSubAccount: () => this.isLoadingSubAccount,
        setActiveTradingAccountAddress: this.setActiveTradingAccountAddress,
        getActiveTradingAccountAddress: () => this.activeTradingAccountAddress,
        generateRootAccountToken: callbacks.generateRootAccountToken,
        fetchAllPortfolios: callbacks.fetchAllPortfolios,
      },
      forceRefresh,
    );
  };

  updateNoncesFromBlockchain = async (address: string) => {
    await accountLoader.updateNoncesFromBlockchain(address, this._allSubAccounts, this.setAllSubAccounts);
  };

  findAndRestoreTradingAccount = async (wallet: Wallet, targetAddress: string, expectedNonce: string) => {
    return accountLoader.findAndRestoreTradingAccount(wallet, targetAddress, expectedNonce, this._allSubAccounts);
  };

  initializeFromStorage = (storedAccounts: SubAccount[]) => {
    connectionFlowService.initializeSubAccountFromStorage(storedAccounts, { setSubAccount: this.setSubAccount });
  };

  selectInitialTradingAccount = (storedAccounts: SubAccount[], savedAddress: string | null) => {
    return connectionFlowService.selectInitialTradingAccount(storedAccounts, savedAddress);
  };

  createSubAccount = async (
    wallet: Wallet,
    callbacks: {
      showToast: (data: { type: "error" | "info" | "success" | "warning"; title: string; content: string }) => void;
    },
  ) => {
    if (!wallet.account) return;

    await connectionFlowService.createSubAccount(
      wallet,
      this._allSubAccounts.get(wallet.account.address.toString()) ?? [],
      {
        setSubAccount: this.setSubAccount,
        setActiveTradingAddress: (addr) => {
          this.activeTradingAccountAddress = addr;
        },
        setAllSubAccounts: this.setAllSubAccounts,
        showToast: callbacks.showToast,
      },
    );
  };

  // ==================== SESSION ====================
  private loadFromSession = async () => {
    const savedAddress = await storageService.getActiveTradingAddress();
    if (savedAddress) {
      this.activeTradingAccountAddress = savedAddress;
    }

    await accountLoader.loadSubAccountsFromSession({
      getAllSubAccounts: () => this._allSubAccounts,
      setSubAccountsLoaded: (v) => {
        this.subAccountsLoaded = v;
      },
    });
  };

  // ==================== RESET ====================
  reset = () => {
    this._allSubAccounts.clear();
    this.subAccountsLoaded = true;
    this.isLoadingSubAccount = false;
    this.activeTradingAccountAddress = null;
    this.subAccount = null;
    this.tempFundingSubAccount = null;
    this.tempTradingSubAccount = null;
    subAccountService.clearCaches();
  };
}
